import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchDemoDictionaryWordApi } from "../../vocab-map/services/vocabMap.services";
import { DemoDictionaryChrome } from "./DemoDictionaryChrome";

function uniqueValues(values) {
  const seen = new Set();
  const unique = [];
  values.forEach((value) => {
    const cleaned = (value || "").trim();
    if (!cleaned || seen.has(cleaned)) return;
    seen.add(cleaned);
    unique.push(cleaned);
  });
  return unique;
}

function groupByPos(entries) {
  const groups = [];
  entries.forEach((entry) => {
    const pos = entry.pos || "";
    const existing = groups.find((group) => group.pos === pos);
    if (existing) existing.entries.push(entry);
    else groups.push({ pos, entries: [entry] });
  });
  return groups;
}

export default function DemoDictionaryWordPage() {
  const [searchParams] = useSearchParams();
  const lex = searchParams.get("lex") || "";

  const wordResult = useQuery({
    queryKey: ["demo-dictionary-word", lex],
    queryFn: () => fetchDemoDictionaryWordApi({ lex }),
    enabled: lex.length >= 1,
    refetchOnWindowFocus: false,
  });

  const entries = wordResult.data?.entries || [];
  const example = wordResult.data?.example || null;
  const groups = groupByPos(entries);

  return (
    <DemoDictionaryChrome variant="topbar">
      {!lex && (
        <p className="py-10 text-sm text-gray-500">
          Choose a word from{" "}
          <Link to="/demo_dictionary" className="underline">
            search
          </Link>
          .
        </p>
      )}
      {lex && wordResult.isFetching && (
        <p className="py-10 text-sm text-gray-500">Loading…</p>
      )}
      {lex && wordResult.isError && (
        <p className="py-10 text-sm text-red-600">Could not load this word.</p>
      )}
      {lex && !wordResult.isFetching && !wordResult.isError && entries.length === 0 && (
        <p className="py-10 text-sm text-gray-500">No entry found for this word.</p>
      )}
      {lex && !wordResult.isFetching && !wordResult.isError && entries.length > 0 && (
        <article className="text-left">
          <h1
            className="text-right text-5xl leading-tight"
            dir="rtl"
            style={{ fontFamily: "var(--font-arabic-title)" }}
          >
            {lex}
          </h1>
          <div className="mt-8 space-y-10">
            {groups.map((group) => {
              const roots = uniqueValues(group.entries.map((entry) => entry.root));
              const phonos = uniqueValues(group.entries.map((entry) => entry.phono));
              const msaLemma = uniqueValues(group.entries.map((entry) => entry.msa_lemma))[0];
              const senses = group.entries.flatMap((entry) => entry.senses || []);
              return (
                <section key={group.pos || "unknown"}>
                  <p className="flex flex-wrap items-baseline justify-start gap-x-4 gap-y-1 text-lg">
                    {group.pos && <span className="italic lowercase">{group.pos}</span>}
                    {roots.length > 0 && (
                      <span dir="rtl" style={{ fontFamily: "var(--font-arabic-title)" }}>
                        ({roots.join("، ")})
                      </span>
                    )}
                    {phonos.length > 0 && (
                      <span className="text-gray-600 dark:text-gray-300">/{phonos.join(", ")}/</span>
                    )}
                  </p>
                  {msaLemma && (
                    <p className="mt-3 text-base">
                      <span className="text-gray-500">MSA: </span>
                      <span dir="rtl" style={{ fontFamily: "var(--font-arabic-title)" }}>
                        {msaLemma}
                      </span>
                    </p>
                  )}
                  {senses.length > 0 && (
                    <div className="mt-6">
                      <h2 className="text-sm font-medium text-gray-500">
                        Gloss of{" "}
                        <span dir="rtl" style={{ fontFamily: "var(--font-arabic-title)" }}>
                          {lex}
                        </span>
                      </h2>
                      <ol className="mt-3 space-y-2 text-left">
                        {senses.map((sense, index) => (
                          <li key={`${sense}-${index}`} className="text-lg">
                            <span className="mr-3 text-gray-500">{index + 1} :</span>
                            {sense}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
          {example && (
            <section className="mt-10">
              <h2 className="text-sm font-medium text-gray-500">
                Examples with{" "}
                <span dir="rtl" style={{ fontFamily: "var(--font-arabic-title)" }}>
                  {lex}
                </span>
              </h2>
              <p
                className="mt-3 text-xl leading-relaxed"
                dir="rtl"
                style={{ fontFamily: "var(--font-arabic-title)" }}
              >
                {(example.tokens || []).map((token, index) => (
                  <span
                    key={`${token}-${index}`}
                    className={index === example.word_index ? "font-bold" : undefined}
                  >
                    {index > 0 ? " " : ""}
                    {token}
                  </span>
                ))}
              </p>
            </section>
          )}
        </article>
      )}
    </DemoDictionaryChrome>
  );
}
